import { request } from '../client';
import { endpoints } from '../endpoints';
import { asList } from './shape';

const clean = (v) => (typeof v === 'string' ? v.trim() : '');

/** The optional profile fields, trimmed, and only the ones that were filled in. */
const optionalFields = (values) => {
  const out = {};
  ['firstName', 'lastName', 'email', 'mobile'].forEach((key) => {
    const v = clean(values[key]);
    if (v) out[key] = v;
  });
  return out;
};

/**
 * Create an account (POST /login/register). Required: `username` (no spaces, unique whatever the case) and
 * `password` (at least 8 characters). Optional: firstName, lastName, email (valid), mobile.
 * Sign-up creates a NEW ORGANISATION (a new `clientId`) and makes this person its admin; `role` and `clientId` are
 * deliberately not sent from this screen. To add a colleague to an existing organisation an admin uses `createUser`.
 * Resolves the new user (201) WITHOUT the password. Failures are ApiErrors: 400 (`details` = one line per problem,
 * e.g. "password: must be at least 8 characters"), 409 "Username is already taken", 403 "Registration is switched off".
 * The user then signs in with the usual login (HTTP 202 quirk below); no token is returned here.
 * @param {{ username: string, password: string, firstName?: string, lastName?: string, email?: string, mobile?: string }} values
 */
export async function registerUser(values) {
  return request(endpoints.auth.register(), { method: 'POST', body: { username: clean(values.username), password: values.password, ...optionalFields(values) } });
}

/**
 * Sign in. A correct and a wrong password BOTH answer HTTP 202, so success is detected by the message, in this one place
 * only; an unknown username is a 404. On success the body also carries the session token:
 * `token` (send as `Authorization: Bearer <token>`, see setAuthTokenProvider), `tokenType`, `expiresIn` (seconds),
 * `userId`, `username` and `role` (`user` | `admin` | `superadmin`).
 * Resolves { ok: true, user, token, expiresAt } (`expiresAt` in ms since the epoch, or null when no expiry was sent) or
 * { ok: false, message }. `user` holds the profile and role but never the token.
 */
export async function login({ username, password }) {
  const res = await request(endpoints.auth.login(), { method: 'POST', body: { username, password } });
  if (res && res.message === 'Auth Successful') {
    const { message, status, token, tokenType, expiresIn, ...user } = res;
    const seconds = Number(expiresIn);
    return { ok: true, user, token: typeof token === 'string' && token ? token : null, expiresAt: Number.isFinite(seconds) && seconds > 0 ? Date.now() + seconds * 1000 : null };
  }
  return { ok: false, message: (res && res.message) || 'Sign in failed.' };
}

/** The signed-in user's profile with `role` (GET /users/me). 401 without a token. */
export const getCurrentUser = (opts) => request(endpoints.auth.me(), opts);

/** The users of my organisation (admin only, 403 for a `user`); a superadmin sees everyone. */
export const listUsers = async (opts) => asList(await request(endpoints.auth.users(), opts));

/**
 * Add a colleague to MY organisation (POST /users, admin only). Required: `username` and `password` (same rules as
 * sign-up); optional `role` (`user` by default, or `admin`), firstName, lastName, email. `clientId` is never sent: the
 * server puts the new user in the admin's own organisation. Errors as for `registerUser`, plus 403 for a non-admin.
 * @param {{ username: string, password: string, role?: 'user' | 'admin', firstName?: string, lastName?: string, email?: string }} values
 */
export const createUser = (values) =>
  request(endpoints.auth.users(), {
    method: 'POST',
    body: { username: clean(values.username), password: values.password, role: values.role === 'admin' ? 'admin' : 'user', ...optionalFields({ firstName: values.firstName, lastName: values.lastName, email: values.email }) },
  });
